import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { QueryClient } from '@tanstack/react-query';

// API Configuration
const API_BASE_URL = (import.meta as any).env.VITE_API_URL || 'http://localhost:8000';
const API_VERSION = '/api/v1';

// Create axios instance with base configuration
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: `${API_BASE_URL}${API_VERSION}`,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 errors (unauthorized)
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Try to refresh token
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            const response = await axios.post(`${API_BASE_URL}${API_VERSION}/auth/refresh`, {
              refresh_token: refreshToken,
            });

            const { access_token } = response.data;
            localStorage.setItem('access_token', access_token);

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return client(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, logout user
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      // Handle other errors
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'An unexpected error occurred';

      // Don't show toast for 401 errors as they're handled above
      if (error.response?.status !== 401) {
        toast.error(errorMessage);
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Create the API client instance
export const apiClient = createApiClient();

// Health check API functions
export const healthApi = {
  // Get all integrations health status
  getAllHealth: async () => {
    const response = await apiClient.get(API_ENDPOINTS.INTEGRATIONS.HEALTH);
    return response.data;
  },

  // Get specific integration health status
  getSpecificHealth: async (integrationType: string) => {
    const response = await apiClient.post(API_ENDPOINTS.INTEGRATIONS.HEALTH_SPECIFIC(integrationType));
    return response.data;
  },
};

// Utility function for comprehensive cache invalidation
export const invalidateAllQueries = (queryClient: QueryClient, workspaceId?: string) => {
  // Invalidate general queries
  queryClient.invalidateQueries({ queryKey: ['agents'] });
  queryClient.invalidateQueries({ queryKey: ['workflows'] });
  queryClient.invalidateQueries({ queryKey: ['workspaces'] });
  queryClient.invalidateQueries({ queryKey: ['integrations'] });
  
  // Invalidate workspace-specific queries if workspaceId is provided
  if (workspaceId) {
    queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] });
    queryClient.invalidateQueries({ queryKey: ['workflows', workspaceId] });
  }
};

// Helper function to handle API errors
export const handleApiError = (error: any): string => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

// Helper function to make authenticated requests
export const makeAuthenticatedRequest = async <T>(
  requestConfig: AxiosRequestConfig
): Promise<T> => {
  try {
    const response = await apiClient(requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// API endpoints configuration
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    VERIFY_TOKEN: '/auth/verify-token',
  },
  // Workspaces
  WORKSPACES: {
    LIST: '/workspaces',
    CREATE: '/workspaces',
    GET: (id: string) => `/workspaces/${id}`,
    UPDATE: (id: string) => `/workspaces/${id}`,
    DELETE: (id: string) => `/workspaces/${id}`,
    SWITCH: (id: string) => `/workspaces/${id}/switch`,
    STATS: (id: string) => `/workspaces/${id}/stats`,
    DUPLICATE: (id: string) => `/workspaces/${id}/duplicate`,
  },
  // Agents
  AGENTS: {
    LIST: '/agents',
    CREATE: '/agents',
    GET: (id: number) => `/agents/${id}`,
    UPDATE: (id: number) => `/agents/${id}`,
    DELETE: (id: number) => `/agents/${id}`,
    EXECUTE: (id: number) => `/agents/${id}/execute`,
    EXECUTIONS: (id: number) => `/agents/${id}/executions`,
    METRICS: (id: number) => `/agents/${id}/metrics`,
    TOOLS: '/agents/tools',
    TOOLS_BY_CATEGORY: (category: string) => `/agents/tools/${category}`,
  },
  // Workflows
  WORKFLOWS: {
    LIST: '/workflows',
    CREATE: '/workflows',
    GET: (id: number) => `/workflows/${id}`,
    UPDATE: (id: number) => `/workflows/${id}`,
    DELETE: (id: number) => `/workflows/${id}`,
    EXECUTE: (id: number) => `/workflows/${id}/execute`,
    EXECUTE_STREAM: (id: number) => `/workflows/${id}/execute-stream`,
    EXECUTIONS: (id: number) => `/workflows/${id}/executions`,
    EXECUTION: (executionId: string) => `/workflows/executions/${executionId}`,
    CANCEL_EXECUTION: (executionId: string) => `/workflows/executions/${executionId}/cancel`,
    EXECUTION_DETAILS: (workflowId: number, executionId: string) => 
      `/workflows/${workflowId}/executions/${executionId}`,
    STATISTICS: '/workflows/statistics',
    SEARCH: '/workflows/search',
  },
  // Integrations
  INTEGRATIONS: {
    LIST: '/integrations',
    CREATE: '/integrations',
    GET: (id: number) => `/integrations/${id}`,
    UPDATE: (id: number) => `/integrations/${id}`,
    DELETE: (id: number) => `/integrations/${id}`,
    TEST: (id: number) => `/integrations/${id}/test`,
    HEALTH: '/integrations/health',
    HEALTH_SPECIFIC: (type: string) => `/integrations/${type}/health`,
  },
  // Monitoring
  MONITORING: {
    SYSTEM_METRICS: '/monitoring/system',
    AGENT_METRICS: '/monitoring/agents',
    WORKFLOW_METRICS: '/monitoring/workflows',
    PERFORMANCE_METRICS: '/monitoring/performance',
    INTEGRATION_HEALTH: '/monitoring/integrations',
    ERROR_LOGS: '/monitoring/errors',
    USAGE_STATS: '/monitoring/usage',
  },
} as const;

// Type-safe API response wrapper
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
}

// Common request types
export interface PaginatedRequest {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
} 